<template>
  <hr-form-dialog
    ref="dialog"
    :title="$t('modals.encryptionModal.encryptionTitle')"
    :blocking="networkOnline"
    @submit="onSubmit"
    @hide="$emit('hide')"
  >
    <!-- The translated copy carries its own markup (<p>, <strong>). -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-html="$t('modals.encryptionModal.encryptionSubtitle')" />
    <hr-password-field
      v-model="encryptionPassword"
      required
      :label="$t('settings.inputPasswordLabel')"
      :email="userDetails.email"
      validate
      strength-indicator
      data-e2e="input-offline-password"
    />
    <hr-password-field
      v-model="encryptionPasswordConfirm"
      required
      :label="$t('settings.inputConfirmPasswordLabel')"
      :confirm="encryptionPassword"
      data-e2e="input-confirm-offline-password"
    />
    <template #actions>
      <hr-confirm-btn
        :disable="!networkOnline"
        type="submit"
        :label="$t('common.buttonSubmit')"
        data-e2e="button-submit-offline-password"
      />
    </template>
  </hr-form-dialog>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { BROADCAST_MESSAGES } from 'src/constants/broadcastChannel';
import {
  HrConfirmBtn, HrFormDialog, HrPasswordField, notifyNegative,
} from 'src/design-system';
import { auth } from 'src/store/auth';
import { broadcastChannel } from 'src/store/broadcastChannel';
import { network } from 'src/store/network';
import { userInterface } from 'src/store/userInterface';
import { createEncryptionKeys, saveEncryptionPassword } from 'src/utils/CryptoUtils';

export default {
  name: 'EncryptionKeysDialog',

  components: {
    HrConfirmBtn, HrFormDialog, HrPasswordField,
  },

  props: {
    skipSync: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['ok', 'hide'],

  data() {
    return {
      encryptionPassword: '',
      encryptionPasswordConfirm: '',
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(network, ['networkOnline']),
    ...mapState(userInterface, ['encryptionKeysDialogOpen']),
  },

  watch: {
    // Handler to close this dialog in all tabs when it's filled in another tab
    encryptionKeysDialogOpen(value, prevValue) {
      if (prevValue && !value) {
        this.hide();
      }
    },
  },

  methods: {
    ...mapActions(auth, ['offlineSync']),
    ...mapActions(broadcastChannel, ['sendBroadcastMessage']),
    ...mapActions(userInterface, ['setEncryptionKeysDialogOpen']),

    show() {
      this.$refs.dialog.show();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    async onCreateNewKeys() {
      try {
        await createEncryptionKeys(this.encryptionPassword);
        await saveEncryptionPassword(this.encryptionPassword);
        this.setEncryptionKeysDialogOpen(false);

        if (this.skipSync) {
          return this.$emit('ok');
        }

        await this.offlineSync({ resetDb: true });
      } catch (err) {
        notifyNegative(err.message);
      } finally {
        this.hide();
        this.sendBroadcastMessage(BROADCAST_MESSAGES.ENCRYPTION_KEYS_FILLED_BROADCAST_MESSAGE);
      }
    },

    async onSubmit() {
      await this.onCreateNewKeys();
    },
  },
};
</script>
